import styled from 'styled-components';
import { useSelector } from 'react-redux';

import {
  selectIsWhatsAppNewCharLimitsFeatureFlagOn,
  selectUserProjectId,
} from 'core/userContext/selectors';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { TextHeader, WhatsAppContentDefinition } from 'modules/graphql';
import { TemplateType } from 'constants/templateTypes';
import { BUTTON_VARIANTS } from 'ui/button';
import { AnimatedRefreshIcon, WarningFill16 } from 'ui/icons';
import { iconColors } from 'ui/theme';
import { PreviewRefreshButton, shortenLinkInMessage, TemplateEditViews } from '../../common';
import { PreviewHeader } from '../../common/PreviewHeader';

import { useWhatsAppPreviewContext } from '../context/WhatsAppPreviewContext';
import { useWhatsAppLinkShortening } from '../context/WhatsAppLinkShorteningContext';
import {
  MAX_FOOTER_LENGTH,
  MAX_HEADER_LENGTH,
  MAX_MESSAGE_LENGTH_BUSINESS,
  MAX_MESSAGE_LENGTH_USER,
  NEW_MAX_MESSAGE_LENGTH_BUSINESS,
} from '../constants';
import {
  WhatsAppPreviewContainer,
  PreviewFooter,
  PreviewCharacterCount,
  PreviewContent,
} from './styles';
import { Preview } from './Preview';

export const TEMPLATE_EDITOR_WHATS_APP_PREVIEW_PANE_ID = 'template-editor-whats-app-preview-pane';

const Row = styled.div`
  display: flex;
`;

export const PreviewPane = () => {
  const {
    onRefreshPreview,
    isLoading: isRefreshing,
    hasLoadedData,
    whatsAppMessage,
    whatsAppHeader,
  } = useWhatsAppPreviewContext();
  const isNewCharLimit = useSelector(selectIsWhatsAppNewCharLimitsFeatureFlagOn);
  const maxBusinessCharLimit = isNewCharLimit
    ? NEW_MAX_MESSAGE_LENGTH_BUSINESS
    : MAX_MESSAGE_LENGTH_BUSINESS;
  const { liveTemplate } = useEditableFieldsContext();
  const { isWhatsAppLinkShorteningEnabled, shortlinkLength, defaultWhatsAppLinkShorteningDomain } =
    useWhatsAppLinkShortening();
  const whatsAppData = liveTemplate?.mediumData as WhatsAppContentDefinition;
  const { view } = useQueryParamContext();
  const projectId = useSelector(selectUserProjectId);

  const whatsAppFooter = whatsAppData?.footer;
  const whatsAppHeaderText = (whatsAppHeader as TextHeader)?.text;
  const isPreviewWithData = view === TemplateEditViews.preview;
  const { templateType } = liveTemplate;
  const charLimit =
    templateType === TemplateType.BusinessInitiated
      ? maxBusinessCharLimit
      : MAX_MESSAGE_LENGTH_USER;

  const shortenLinkInTextContent = (textContent: string = '') => {
    return (
      shortenLinkInMessage(
        textContent,
        whatsAppData?.trackingDomain ?? defaultWhatsAppLinkShorteningDomain ?? '',
        projectId,
        shortlinkLength,
        isWhatsAppLinkShorteningEnabled,
      ) || ''
    );
  };

  const messageMaybeWithShortenLink = shortenLinkInTextContent(whatsAppMessage);
  const headerTextMaybeWithShortenLink = shortenLinkInTextContent(whatsAppHeaderText);
  const footerMaybeWithShortenLink = shortenLinkInTextContent(whatsAppFooter);

  const msgOverLimit = (messageMaybeWithShortenLink?.length ?? 0) > charLimit;
  return (
    <WhatsAppPreviewContainer
      data-test={TEMPLATE_EDITOR_WHATS_APP_PREVIEW_PANE_ID}
      isPreviewWithData={isPreviewWithData}
    >
      <PreviewHeader />
      <PreviewContent
        className="template-editor-whats-app-preview-content"
        data-test="template-editor-whats-app-preview-content"
      >
        <Preview />
      </PreviewContent>
      {isPreviewWithData && (
        <>
          <PreviewRefreshButton
            onClick={() => onRefreshPreview()}
            variant={BUTTON_VARIANTS.SECONDARY}
            data-test="template-editor-whatsapp-preview-refresh-button"
          >
            <AnimatedRefreshIcon $isAnimating={isRefreshing} />
          </PreviewRefreshButton>
          <PreviewFooter>
            {!hasLoadedData ? (
              <PreviewCharacterCount>
                {'Load user data to view character count.'}
              </PreviewCharacterCount>
            ) : (
              <Row>
                {whatsAppHeaderText && (
                  <PreviewCharacterCount>{`Header: ${
                    headerTextMaybeWithShortenLink?.length ?? 0
                  }/${MAX_HEADER_LENGTH} characters`}</PreviewCharacterCount>
                )}
                {whatsAppMessage && (
                  <PreviewCharacterCount className={msgOverLimit ? 'isWarning' : ''}>
                    {msgOverLimit && <WarningFill16 color={iconColors.iconWarning} />}{' '}
                    {`Body: ${messageMaybeWithShortenLink?.length ?? 0}/${charLimit} characters`}
                  </PreviewCharacterCount>
                )}
                {whatsAppFooter && (
                  <PreviewCharacterCount>{`Footer: ${
                    footerMaybeWithShortenLink?.length ?? 0
                  }/${MAX_FOOTER_LENGTH} characters`}</PreviewCharacterCount>
                )}
              </Row>
            )}
          </PreviewFooter>
        </>
      )}
    </WhatsAppPreviewContainer>
  );
};

export default PreviewPane;
